<template>
  <form-layout
    data-test="readonly-job-side-sheet"
    :title="title ?? ''"
    :is-loading="isFetching"
    @on-dismiss="dismiss"
  >
    <template #top-sheet>
      <RepeatJobsInfoDisplay
        class="mb-6"
        :job-id="jobUuid"
        :job-request-id="jobRequestId"
        :enable-request-sidesheet="
          job?.activity?.stopConfirmationRequirement === StopConfirmationRequirement.Required
        "
      />
      <JobTripMap
        v-if="showTripMap"
        :job-id="jobUuid"
        :job-number="props.id"
        :map-link="mapLink"
        @distance-calculated="setTripDistance"
      ></JobTripMap>
    </template>

    <JobErrorBanner />

    <template #label>
      <v-row v-if="job">
        <v-col>
          <v-icon :color="job.priority.color">
            {{ job.priority.icon }}
          </v-icon>
        </v-col>
        <v-col>
          <job-status-chip
            v-if="job.status"
            data-test="readonly-job-status"
            :status="finalStatus"
          />
        </v-col>
      </v-row>
    </template>

    <template v-if="!isFetching" #breadcrumbs>
      <project-breadcrumbs
        v-if="job?.projectId"
        data-test="readonly-job-title"
        :project-id="job.projectId"
        :job-display-name="displayedJobId"
        :route="projectSideSheetRoute"
      />

      <vs-text v-else class="displayed-job-id">{{ displayedJobId }}</vs-text>
    </template>

    <vs-wrapper space="large">
      <!-- Assignment -->
      <v-row data-test="readonly-assignment" align="center">
        <v-col class="shrink pr-2">
          <v-icon>navigation</v-icon>
        </v-col>
        <v-col>
          <vs-text>{{ subtitle }}</vs-text>
          <vs-text v-if="job && job.crew.length > 0">Operated by {{ jobCrewNames }} </vs-text>
        </v-col>
      </v-row>

      <job-durations-display v-if="job" :job="job" :show-edit-button="showEditButton" />

      <JobStopsAndInventoriesDisplay
        v-if="job"
        :job-id="job.uuid"
        :job-number="job.id"
        :show-edit-button="showEditButton"
      />

      <!-- Distance -->
      <v-row v-if="isJobCompleted" align="center" data-test="readonly-trip-distance">
        <v-col class="shrink pr-2">
          <v-icon>route</v-icon>
        </v-col>
        <v-col class="grow">
          <vs-text>{{ formattedTripDistance }}</vs-text>
        </v-col>
        <v-col v-if="mapLink" class="shrink">
          <div>
            <v-tooltip left>
              <template #activator="{ on }">
                <div v-on="on">
                  <vs-button
                    data-test="see-on-map"
                    icon="mdi-map-outline"
                    type="secondary"
                    small
                    :to="mapLink"
                  />
                </div>
              </template>
              <span>View on map</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>

      <!-- Actions -->
      <readonly-job-form-action-bar
        v-if="job && job.status === JobStatus.COMPLETED"
        :job-model="job"
        :company="company"
        @refresh-job-history="refreshJobHistory"
        @handle-snackbar="setSnackbar"
        @open-email-dialog="setEmailDialogVisibility(true)"
        @update-readonly-job="handleUpdateReadonlyJob"
        @on-click-edit="$emit('on-click-edit')"
      />
      <combined-action-bar
        v-else-if="job"
        :job-id="job.id"
        :company="company"
        :is-single-line="false"
        @on-click="handleActionBarClick"
        @handle-snackbar="setSnackbar"
      ></combined-action-bar>

      <job-prediction-details
        v-if="job && (job.status === JobStatus.ACTIVE || job.status === JobStatus.PAUSED)"
        :job-number="jobNumber"
      />
      <completed-job-prediction-details
        v-if="job && job.status === JobStatus.COMPLETED"
        :job-id="job.uuid"
        :job-activated-at="job.activeTime"
        :job-completed-at="job.completedTime"
      />

      <!-- Details -->
      <div class="pt-2">
        <v-row class="pb-1">
          <v-col>
            <vs-heading type="overline" class="pt-2">Details</vs-heading>
          </v-col>
          <v-col v-if="showEditButton" class="shrink">
            <vs-button label="Edit" small type="secondary" @click="openJobDetailsEdit" />
          </v-col>
        </v-row>
        <vs-text type="caption">{{ job?.details }}</vs-text>
      </div>

      <vs-wrapper space="small">
        <v-row>
          <v-col>
            <vs-text type="alt-paragraph">Requester</vs-text>
          </v-col>
          <v-col>
            <vs-text>{{ jobRequesterName }}</vs-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <vs-text type="alt-paragraph">Division</vs-text>
          </v-col>
          <v-col>
            <vs-text>{{ jobDivisionPath }}</vs-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <vs-text type="alt-paragraph">Followers</vs-text>
          </v-col>
          <v-col>
            <vs-text>{{ followersLabel }}</vs-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <vs-text type="alt-paragraph">Cost Center</vs-text>
          </v-col>
          <v-col>
            <vs-text>{{ job?.costCenter }}</vs-text>
            <vs-text v-if="costCenterDescription" type="caption"
              >{{ costCenterDescription }}
            </vs-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <submission-field
              v-if="job"
              :flattened-fields="flattenedFormFields"
              :submission-data="job.customData"
            />
          </v-col>
        </v-row>
      </vs-wrapper>

      <JobTagsDisplay :job-id="jobUuid" />

      <!-- Forms -->
      <job-form
        v-if="job"
        :job-form-associations="job.activity?.formAssociations ?? []"
        :job-form-submissions="job.formSubmissions"
        :job-is-approved="job.isApproved"
        @edit-job-form-submission="(e) => editJobFormSubmission(e.formUuid, e.formSubmissionUuid)"
        @fill-job-form-submission="(e) => fillJobFormSubmission(e.formUuid)"
      />

      <assignment-form-requirements
        v-if="job"
        :hub-id="job.hubId"
        :form-requirements="job.assignmentFormRequirements"
        :service-provider-business-unit-id="job.serviceProviderBusinessUnitId"
        :is-new-job="false"
        readonly
        @fill-form-requirement="fillFormRequirement"
        @view-form-requirement-submission="viewFormRequirementSubmission"
      />

      <!-- attachments -->
      <attachments
        v-if="job"
        :job-uuid="job.uuid"
        :client-only="props.id === null"
        :job-attachments="job.attachments"
        :attachment-storage-id="job.attachmentsStorageId"
        :enable-delete="!job.isApproved"
      >
        <template>
          <attachment-card
            v-for="formSubmission in adhocFormSubmissions"
            :key="`formSubmission-${formSubmission.formSubmissionUuid}`"
            icon="assignment_turned_in"
            :primary-text="formSubmission.formName"
            :secondary-text="getAdhocFormSubmissionSecondaryText(formSubmission)"
          >
            <template #primary-text>
              <a
                class="text-decoration-underline"
                @click.prevent="showFormSubmission(formSubmission.formSubmissionId)"
                >{{ formSubmission.formName }}</a
              >
            </template>
          </attachment-card>
        </template>
      </attachments>

      <vs-wrapper v-if="job && !job.isApproved" space="small">
        <v-row dense>
          <v-col class="shrink" align-self="center">
            <vs-text class="add-text">Add:</vs-text>
          </v-col>
          <v-col class="shrink" align-self="center">
            <attachment-picker
              :job-uuid="job.uuid"
              :job-is-new="props.id === null"
              data-test="add-attachments"
              class="pl-0"
              @unsupported-file-selected="unsupportedFileSelected"
              @error-uploading-attachment="errorUploadingAttachment"
            />
          </v-col>
          <v-divider class="my-3" vertical />
          <v-col class="shrink" align-self="center">
            <vs-button
              icon="mdi-clipboard-text-outline"
              label="Form"
              type="tertiary"
              small
              @click="showFormSelection"
            />
          </v-col>
        </v-row>
      </vs-wrapper>

      <job-references-card v-if="job" :job-id="job.uuid" />

      <line-items-card
        v-if="job && canViewLineItems && jobUuid"
        :job-id="jobUuid"
        :job-approval-status="job.approvalStatus"
        :readonly="!lineItemsEditPermission.allowed"
        :edit-permission="lineItemsEditPermission"
        :assigned-to-service-provider-id="job.serviceProviderBusinessUnitId"
        :hub-id="job.hubId"
        show-batch-link
        :batch-id="job.invoiceId"
        :line-items-when-signed="job.signature?.metadata.itemsWhenSigned ?? []"
      />

      <job-signature
        v-if="job && jobUuid"
        :job-id="jobUuid"
        :job-status="job.status"
        :job-approval-status="job.approvalStatus"
        :signature="job.signature"
        @clear-signature="clearSignature"
      />

      <!-- Log -->
      <job-activity-log
        v-if="job && jobUuid"
        :job-number="job.id"
        :job-id="jobUuid"
        :job-hub-id="job.hubId"
        :start-time="job.startTime"
        :end-time="job.endTime"
        :custom-field-definitions="flattenedFormFields"
      />
    </vs-wrapper>

    <!-- Dialogs -->
    <send-email-dialog
      v-model="isEmailDialogVisible"
      persistent
      :job-id="props.id"
      @set-email-dialog-visibility="setEmailDialogVisibility"
      @notification-sent="handleNotificationSent"
    />

    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
        {{ customSnackbarMessage ?? snackbarMessages[snackbarMessageKey ?? ''] }}
      </vs-snackbar>
    </template>

    <template #bottom-sheet>
      <vs-bottom-sheet
        v-if="job"
        :is-visible="isBottomSheetVisible"
        :full-height="
          !!selectedFormSubmissionId ||
          !!selectedFormRequirement ||
          jobBottomSheetStatesStore.isCompletedJobBottomSheetFullHeight
        "
      >
        <project-selection-page
          v-if="bottomSheetStates.isProjectBottomSheetVisible"
          :job-id="job.id"
          :owner-id="job.hubId"
          @job-added-success="addToProject"
          @job-added-failure="handleJobAddedFailure"
        />

        <job-planned-actual-times-edit
          v-if="bottomSheetStates.isEditingPlannedActualTimesBottomSheetVisible"
          :job="job"
          :job-hub-id="job.hubId"
          @updated="jobPlannedActualTimesUpdated"
        />

        <job-details-edit
          v-if="bottomSheetStates.isEditingJobDetailsBottomSheetVisible"
          :job="job"
          :job-hub-id="job.hubId"
          :schema="rootSchema"
          :has-newer-custom-fields-schema="hasNewerCustomFieldsSchema"
          @details-updated="jobDetailsUpdated"
          @update-schema-button-clicked="updateSchemaButtonClicked"
        />

        <send-for-approval-bottom-sheet
          v-if="bottomSheetStates.isSendingEmailForApprovalBottomSheetVisible"
          :job="job"
          @submitted-for-approval="submittedForApproval"
        />

        <form-submission
          v-if="selectedFormSubmissionId"
          :form-submission-id="selectedFormSubmissionId"
          :can-delete="canDeleteFormSubmissions"
          @dismiss="closeFormSubmission"
          @edited="updatedAttachedFormSubmission"
        />

        <form-submission
          v-else-if="selectedFormRequirement && !isFillingFormRequirementSubmission"
          :form-submission-id="selectedFormRequirement?.formSubmissionId ?? ''"
          @dismiss="closeFormRequirementSubmission"
          @edit="updatedFormRequirementSubmission"
        />

        <new-form-selection-bottom-sheet
          v-if="bottomSheetStates.isSelectingFormBottomSheetVisible"
          data-test="new-from-selection"
          :forms="fillableForms"
          :small="false"
          :hub-id="job.hubId"
          @select="selectNewForm"
          @dismiss="jobBottomSheetStatesStore.clearJobBottomSheetStates"
        />

        <vs-wrapper v-if="bottomSheetStates.formSubmissionBottomSheetState.isVisible">
          <form-submission
            v-if="!bottomSheetStates.formSubmissionBottomSheetState.isEditing"
            :form-id="bottomSheetStates.formSubmissionBottomSheetState.formUuid ?? ''"
            :form-submission-id="
              bottomSheetStates.formSubmissionBottomSheetState.formSubmissionUuid ?? ''
            "
            :can-delete="false"
            @dismiss="closeSchemaBasedForm"
          />
          <schema-based-form
            v-else
            :form-id="bottomSheetStates.formSubmissionBottomSheetState.formUuid ?? ''"
            :form-submission-id="
              bottomSheetStates.formSubmissionBottomSheetState.formSubmissionUuid ?? undefined
            "
            :business-unit-id="job.hubId"
            @dismiss="closeSchemaBasedForm"
            @submitted="
              (formSubmissionUuid, isEditingJobFormSubmission) =>
                isEditingJobFormSubmission
                  ? updatedAttachedFormSubmission()
                  : processAttachFormSubmission(formSubmissionUuid)
            "
          />
        </vs-wrapper>

        <schema-based-form
          v-else-if="selectedFormRequirement && isFillingFormRequirementSubmission"
          :form-id="selectedFormRequirement?.formId ?? ''"
          :business-unit-id="job.hubId"
          @dismiss="closeFillFormRequirement"
          @submitted="attachFormRequirement"
        />

        <line-item-form
          v-if="bottomSheetStates.isLineItemBottomSheetVisible"
          :job-id="job.uuid"
          :job-number="job.id"
          :assigned-to-service-provider-id="job.serviceProviderBusinessUnitId"
          :hub-id="job.hubId"
          :batch-id="job.invoiceId"
          @updated="refreshJobHistory"
        />

        <batch-selection-page
          v-if="bottomSheetStates.isBatchBottomSheetVisible && jobUuid"
          :job-uuid="jobUuid"
          :hub-id="job.hubId"
          @job-added-success="handleAddToBatchSuccess"
          @job-added-failure="handleJobAddedFailure"
        />

        <approval-rejection-form
          v-if="bottomSheetStates.isRejectingApprovalBottomSheetVisible"
          :job-id="job.uuid"
          @approval-rejected-success="handleApprovalRejectedSuccess"
        />

        <JobSignatureForm
          v-if="bottomSheetStates.isSignatureBottomSheetVisible && jobUuid"
          :job-id="jobUuid"
          @signature-added="addSignature"
        />

        <JobTagsEditor
          v-if="bottomSheetStates.isTagBottomSheetVisible"
          :job-id="jobUuid"
          @tags-updated="jobTagsUpdated"
        />

        <ReceiptDetailsBottomSheet
          v-if="bottomSheetStates.receiptDetailsBottomSheetStates.isVisible && jobUuid"
          :job-id="jobUuid"
          :job-number="jobNumber"
        />

        <ActualSkippedStopDetailsBottomSheet
          v-if="bottomSheetStates.actualSkippedStopDetailsBottomSheetStates.isVisible && jobUuid"
          :job-id="jobUuid"
          :job-number="jobNumber"
        />

        <StopConfirmationBottomSheet
          v-if="bottomSheetStates.stopConfirmationBottomSheetStates.isVisible && jobUuid"
          :job-id="jobUuid"
          :job-number="jobNumber"
          :job-activity-id="job.activity?.id"
        />

        <JobRequestedStopFormBottomSheet
          v-if="bottomSheetStates.requestedStopFormBottomSheetVisible && jobUuid"
          :job-id="jobUuid"
          :job-number="jobNumber"
          :job-activity-id="job.activity?.id"
          @stops-updated="jobLocationsAndInventoryUpdated"
        />
        <template #top-layer>
          <portal-target
            v-if="bottomSheetStates.isCreatingLocationOnTheFlyBottomSheetVisible"
            name="job-on-the-fly-location-form-bottom-sheet"
          />
        </template>
      </vs-bottom-sheet>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import length from '@turf/length'
import { computed, onBeforeUnmount, ref, toRef, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { createNamespacedHelpers, useGetters, useStore } from 'vuex-composition-helpers'

import { useGetJobGeoJson } from '@/api/field-view'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { useJobMapLink } from '@/components/tickets/common/useJobMapLink'
import Job, { APPROVED, COMPLETED, JobStatus, NO_APPROVAL, PENDING_APPROVAL } from '@/models/job'
import JobAssignmentFormRequirement from '@/models/job-assignment-form-requirement'
import { CustomFieldsSchema } from '@/models/types/custom-fields-schema'
import { TripMapDistanceUnits } from '@/store/modules/organization-config'
import { kmToMilesConversion } from '@/utils/constants'
import { formatDateTime } from '@/utils/date-formatting'
import { extractFieldsFromSchema } from '@/utils/field-schema-helper'
import { getJobGeoJsonCacheTime } from '@/utils/job-geojson-cache-time'

import BatchSelectionPage from '@/components/batches/BatchSelectionPage.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import FormSubmission from '@/components/forms/FormSubmission.vue'
import NewFormSelectionBottomSheet from '@/components/forms/NewFormSelectionBottomSheet.vue'
import SchemaBasedForm from '@/components/forms/SchemaBasedForm.vue'
import ApprovalRejectionForm from '@/components/jobs/ApprovalRejectionForm.vue'
import JobSignature from '@/components/jobs/sections/JobSignature.vue'
import JobSignatureForm from '@/components/jobs/JobSignatureForm.vue'
import LineItemForm from '@/components/jobs/line-items/LineItemForm.vue'
import { lineItemSidesheetPermission } from '@/components/jobs/line-items/lineItemActionPermissions'
import LineItemsCard from '@/components/jobs/line-items/LineItemsCard.vue'
import ProjectBreadcrumbs from '@/components/project/ProjectBreadcrumbs.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { SnackType } from '@/components/vision/SnackBarType'
import VsText from '@/components/vision/VsText.vue'
import JobActivityLog from '@/components/tickets/history/JobActivityLog.vue'
import JobPredictionDetails from '@/components/jobs/JobPredictionDetails.vue'
import CompletedJobPredictionDetails from '@/components/jobs/CompletedJobPredictionDetails.vue'

import AssignmentFormRequirements from './AssignmentFormRequirements.vue'
import AttachmentCard from './AttachmentCard.vue'
import AttachmentPicker from './AttachmentPicker.vue'
import Attachments from './Attachments.vue'
import JobDetailsEdit from './JobDetailsEdit.vue'
import JobPlannedActualTimesEdit from './JobPlannedActualTimesEdit.vue'
import JobStatusChip from './JobStatusChip.vue'
import JobTripMap from './JobTripMap.vue'
import ReadonlyJobFormActionBar from './ReadonlyJobFormActionBar.vue'
import SendEmailDialog from './SendEmailDialog.vue'
import SendForApprovalBottomSheet from './SendForApprovalBottomSheet.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import JobReferencesCard from '@/components/jobs/references/JobReferencesCard.vue'
import JobTagsEditor from '@/components/jobs/JobTagsEditor.vue'
import JobTagsDisplay from '@/components/jobs/JobTagsDisplay.vue'
import RepeatJobsInfoDisplay from './RepeatJobsInfoDisplay.vue'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import VsButton from '@/components/vision/VsButton.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import JobStopsAndInventoriesDisplay from '@/components/tickets/StopsAndInventories/JobStopsAndInventoriesDisplay.vue'
import ReceiptDetailsBottomSheet from '@/components/tickets/StopsAndInventories/ReceiptDetailsBottomSheet.vue'
import StopConfirmationBottomSheet from '@/components/tickets/StopsAndInventories/StopConfirmationBottomSheet.vue'
import JobRequestedStopFormBottomSheet from './StopsAndInventories/JobRequestedStopFormBottomSheet.vue'
import { useQueryClient } from '@tanstack/vue-query'
import {
  buildRootSchema,
  useExtensionSchemaId,
} from '@/components/tickets/common/useExtensionSchemaId'
import {
  JOBS_FILTERED_COUNT_QUERY_KEY,
  JOBS_FILTERED_QUERY_KEY,
} from '../jobs/job-search-page/useFilterJobsQuery'
import { useHubContextProvider } from '@/common/useHubs'
import JobDurationsDisplay from '@/components/tickets/JobDurationsDisplay.vue'
import CombinedActionBar from '@/components/tickets/CombinedActionBar.vue'
import {
  attachFormSubmissionToStop,
  useBulkArchiveMutation,
  useUnarchiveMutation,
} from '@/api/jobs'
import { ActionClickData } from '@/components/tickets/ActionBarActions/ActionClickData'
import { ActionType } from '@/components/tickets/ActionBarActions/ActionTypes'
import { removeJobFromProjectMutation } from '@/api/useProjectsMutations'
import { useRemoveJobsFromInvoiceMutation } from '@/api/invoice'
import { snackbarMessages } from './SnackbarMessages'
import ProjectSelectionPage from '@/components/project/ProjectSelectionPage.vue'
import JobErrorBanner from '@/components/tickets/JobErrorBanner.vue'
import { useParentRouteName } from '../jobs/composables/useParentRouteName'
import JobForm from './JobForm.vue'
import moment from 'moment/moment'
import { computeFormsWithSubmissionInfo, useGetAdhocFormSubmissions } from './JobForm'
import { getFollowerNames } from './JobFollowersPicker/resolveJobFollowers'
import { BUCKET_COUNT_QUERY_KEY } from '@/components/jobs/job-search-page/useJobBucketCounts'
import ActualSkippedStopDetailsBottomSheet from './StopsAndInventories/ActualSkippedStopDetailsBottomSheet.vue'
import { StopConfirmationRequirement } from '@/models/activity'

const props = withDefaults(
  defineProps<{
    id: number
    isFetchingOverride?: boolean
  }>(),
  {
    isFetchingOverride: undefined,
  },
)

useHubContextProvider(computed(() => job.value?.hubId))

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const isEmailDialogVisible = ref(false)
const isFillingFormRequirementSubmission = ref(false)
const selectedFormSubmissionId = ref<string | null>(null)
const selectedFormRequirement = ref<JobAssignmentFormRequirement | null>(null)
const isSnackbarVisible = ref(false)
const customSnackbarMessage = ref()
const tripDistance = ref<number | undefined>(undefined)
const snackbarMessageKey = ref<string | null>(null)
const snackbarType = ref<SnackType>(SnackType.INFO)

const store = useStore()
const route = useRoute()

const job = computed<Job | undefined>(() => store.getters['jobs/getById'](props.id))
const jobUuid = computed(() => job.value?.uuid)
const jobRequestId = computed(() => job.value?.jobRequestId)

const { parentRouteName } = useParentRouteName()

const { showTripMap, mapLink } = useJobMapLink({
  jobNumber: computed(() => job.value?.id),
  jobStatus: computed(() => job.value?.status),
  parentRouteName: computed(() => parentRouteName.value ?? ''),
})

const {
  currentExtensionSchema,
  currentExtensionSchemaId,
  useLatestCustomFieldsSchema,
  resetCustomFieldsSchemaId,
  hasNewerCustomFieldsSchema,
} = useExtensionSchemaId({
  originalExtensionSchemaId: computed(() => job.value?.extensionSchemaId),
  resourceTypeActivityId: computed(() => job.value?.activityId),
})

const jobNumber = toRef(props, 'id')
const shouldFetchGeoJsonManually = computed(() => !showTripMap.value)
const cacheTime = computed(() => getJobGeoJsonCacheTime(job.value?.status))
const { data: jobGeoJson } = useGetJobGeoJson(jobNumber, {
  enabled: shouldFetchGeoJsonManually,
  cacheTime,
})
const { currentOrg } = useCurrentOrg()

const setTripDistance = (distance: number) => {
  tripDistance.value = distance
}

watchEffect(() => {
  if (jobGeoJson.value && jobGeoJson.value.features.length > 0) {
    const lineStrings = jobGeoJson.value.features.filter((f) => f.properties?.Type === 'Route')

    const totalDistance = lineStrings.reduce((a, b) => a + length(b), 0)

    setTripDistance(totalDistance)
  }
})

const { lineItems: lineItemsFeatureFlag } = useGetters('featureFlags', ['lineItems'])

const registeredTimeouts: ReturnType<typeof setTimeout>[] = []
const registerTimeout = (fn: () => void, ms: number) => {
  const id = setTimeout(fn, ms)
  registeredTimeouts.push(id)
  return id
}

const isFetching = computed(() =>
  props.isFetchingOverride !== undefined ? props.isFetchingOverride : false,
)

onBeforeUnmount(() => {
  registeredTimeouts.forEach((id) => clearTimeout(id))
  registeredTimeouts.splice(0)
})

const bottomSheetStates = computed(() => jobBottomSheetStatesStore.jobBottomSheetStates)

const getUserBySid = computed(() => store.getters['users/getBySid'])
const allUsers = computed(() => store.getters['users/getAllUsers'])
const canModifyCompletedJobsPendingApproval = computed<(hubId: string) => boolean>(
  () => store.getters['permissions/canModifyCompletedJobsPendingApproval'],
)
const canModifyCompletedJobsWithNoApproval = computed<(hubId: string) => boolean>(
  () => store.getters['permissions/canModifyCompletedJobsWithNoApproval'],
)

const getBusinessUnitById = computed(() => store.getters['businessUnits/getBusinessUnitById'])
const getTaskById = computed(() => store.getters['tasks/getById'])
const getCostCentersForHub = computed(() => store.getters['costCenters/getCostCentersForHub'])
const getForms = computed(() => store.getters['forms/getAllVisible'])
const getRolesForBusinessUnit = computed(() => store.getters['permissions/getRolesForBusinessUnit'])
const getByChildHubId = computed(() => store.getters['companies/getByChildHubId'])
const tripMapDistanceUnit = computed<TripMapDistanceUnits>(
  () => store.state.organizationConfig.tripMapDistanceUnit,
)

const isBottomSheetVisible = computed<boolean>(
  () =>
    !!selectedFormSubmissionId.value ||
    !!selectedFormRequirement.value ||
    jobBottomSheetStatesStore.isCompletedJobBottomSheetVisible,
)

const displayedJobId = computed(() => `Job #${route.params.id} · ${company.value?.shortCode}`)

const finalStatus = computed(() => {
  if (!job.value) return ''
  return job.value.approvalStatus !== NO_APPROVAL
    ? job.value.approvalStatus
    : job.value.status.trim()
})

const projectSideSheetRoute = computed(() => {
  const routePrefix = route.name?.split('-')[0]
  return `${routePrefix}-project`
})

const title = computed<string | undefined>(() => job.value?.activity?.taskName)

const subtitle = computed(() => {
  if (!job.value || !job.value.resourceType || !job.value.serviceProvider) return ''

  // Requesters can't see the assigned resources
  if (!job.value.unit)
    return `Assigned to ${job.value.resourceType.name} · ${job.value.serviceProvider.shortCode}`
  return `Assigned to ${job.value.resourceType.name} · ${job.value.unit.name} · ${job.value.serviceProvider.shortCode}`
})

const jobCrewNames = computed(() => {
  if (!job.value) return ''
  const crewNames = job.value.crew.map((u) => getUserBySid.value(u)?.name)

  if (crewNames.length === 1) return crewNames[0]
  else return `${crewNames.slice(0, -1).join(',')} and ${crewNames[crewNames.length - 1]}`
})

const isJobCompleted = computed(() => {
  return job.value?.status === JobStatus.COMPLETED
})
const showEditButton = computed(() => {
  if (!job.value) return false
  if (!isJobCompleted.value) return false
  return (
    !jobIsApproved.value &&
    ((job.value.isPendingApproval &&
      canModifyCompletedJobsPendingApproval.value(job.value.hubId)) ||
      canModifyCompletedJobsWithNoApproval.value(job.value.hubId))
  )
})

const costCenterDescription = computed(() => {
  return (
    getCostCentersForHub.value(job.value?.hubId).find((cc) => cc.label === job.value?.costCenter)
      ?.description ?? ''
  )
})

const jobIsApproved = computed(() => job.value?.approvalStatus === APPROVED)
const jobRequesterName = computed(
  () => getUserBySid.value(job.value?.customerSid)?.name ?? 'Unknown User',
)
const hub = computed(() => getBusinessUnitById.value(job.value?.hubId))
const company = computed(() => getByChildHubId.value(job.value?.hubId))
const jobDivisionPath = computed(() => {
  const hubString = hub.value ? `${hub.value.name} > ` : ''

  const orgString = company.value ? `${company.value.name} > ` : ''

  return `${orgString}${hubString}${job.value?.division?.name}`
})

const followersLabel = computed(() =>
  job.value
    ? getFollowerNames(job.value, allUsers.value, getUserBySid.value).join(', ')
    : undefined,
)

const rootSchema = computed<CustomFieldsSchema>(() => {
  if (!job.value) {
    return {
      schema: {} as any,
      uiSchema: {},
    }
  }

  return buildRootSchema(
    job.value.division,
    job.value.activity ? getTaskById.value(job.value.activity.taskId) : undefined,
    store.getters['schemas/getJobCustomFieldsSchema'],
    currentExtensionSchemaId.value,
    currentExtensionSchema.value,
  )
})

const schema = computed(() => rootSchema.value.schema)
const uiSchema = computed(() => rootSchema.value.uiSchema)

const flattenedFormFields = computed(() => {
  const formFields = {}
  extractFieldsFromSchema(schema.value, uiSchema.value, formFields)
  return formFields
})

const canDeleteFormSubmissions = computed(() => {
  if (!job.value) return false
  if (job.value.approvalStatus === PENDING_APPROVAL) {
    return canModifyCompletedJobsPendingApproval.value(job.value.hubId)
  }
  return false
})

const fillableForms = computed(() => {
  return getForms.value
    .filter((f) => f.fillable)
    .filter((f) => {
      const accessGrant = f.accessGrants.find(
        (ag) => ag.businessUnitId === job.value?.division?.ownerId,
      )
      if (!accessGrant) return false

      const userRoles = getRolesForBusinessUnit.value(job.value?.division.ownerId)

      return userRoles.some((role) => !accessGrant.roleRestrictions.includes(role))
    })
})

const { getAdhocFormSubmissionsWithFormNames } = useGetAdhocFormSubmissions()

const adhocFormSubmissions = computed(() => {
  if (!job.value) return []
  return getAdhocFormSubmissionsWithFormNames(
    job.value.formSubmissions,
    job.value.activity?.formAssociations ?? [],
  )
})

const formsWithSubmissionInfo = computed(() => {
  if (!job.value || !job.value.activity) return []
  return computeFormsWithSubmissionInfo(
    job.value.formSubmissions,
    job.value.activity.formAssociations,
  )
})

const totalDistance = computed<number>(() => {
  if (!job.value) return 0
  const jobDistanceKm =
    job.value.totalDistance > 0 ? job.value.totalDistance / 1000 : tripDistance.value

  return tripMapDistanceUnit.value === TripMapDistanceUnits.MILES
    ? (jobDistanceKm ?? 0) * kmToMilesConversion
    : (jobDistanceKm ?? 0)
})

const formattedTripDistance = computed<string>(() => {
  if (tripDistance.value === undefined) {
    return 'Traveled ~'
  }

  const unitLabel =
    tripMapDistanceUnit.value === TripMapDistanceUnits.MILES ? 'miles' : 'kilometers'

  return `Traveled ${totalDistance.value.toFixed(1)} ${unitLabel}`
})

const canViewLineItems = computed(() => {
  if (!job.value || !lineItemsFeatureFlag.value) return false
  if (job.value.serviceProviderBusinessUnitId === null) return false

  const hub = getBusinessUnitById.value(job.value.hubId)
  const isAssignedInternally =
    !!hub?.ownerId && job.value.serviceProviderBusinessUnitId === hub.ownerId

  return !isAssignedInternally
})

const lineItemsEditPermission = computed(() =>
  lineItemSidesheetPermission(
    job.value?.serviceProviderBusinessUnitId,
    currentOrg.value?.id,
    !!job.value?.isApproved,
  ),
)

const { useActions } = createNamespacedHelpers('jobs')
const { attachFormSubmissionId, attachFormRequirementSubmission, fetchJob, updateJobStatus } =
  useActions([
    'attachFormSubmissionId',
    'attachFormRequirementSubmission',
    'fetchJob',
    'updatePriority',
    'updateJobStatus',
  ])
const router = useRouter()

const emit = defineEmits<{
  (e: 'dismiss')
  (e: 'job-saved', data: { jobNumber: number })
  (e: 'on-click-edit')
}>()

const dismiss = function () {
  emit('dismiss')
}

const setSnackbarData = function ({
  type = SnackType.INFO,
  messageKey = null,
  customMessage = null, // customMessage will override messageKey unless customMessage = null
}: {
  type?: SnackType
  messageKey?: string | null
  customMessage?: string | null
}) {
  snackbarType.value = type
  if (customMessage) {
    customSnackbarMessage.value = customMessage
  } else {
    snackbarMessageKey.value = messageKey
    customSnackbarMessage.value = null
  }
  isSnackbarVisible.value = true
}

const jobDetailsUpdated = async function () {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  registerTimeout(() => setSnackbarData({ messageKey: 'detailsUpdated' }), 250)
}

const jobPlannedActualTimesUpdated = async function () {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  registerTimeout(() => setSnackbarData({ messageKey: 'plannedOrActualTimesUpdated' }), 250)
}

const addSignature = () => {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  registerTimeout(() => setSnackbarData({ messageKey: 'signatureAdded' }), 250)
}

const clearSignature = () => {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  registerTimeout(() => setSnackbarData({ messageKey: 'signatureCleared' }), 250)
}

const jobTagsUpdated = async () => {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  registerTimeout(() => setSnackbarData({ messageKey: 'tagsUpdated' }), 250)
}

const jobLocationsAndInventoryUpdated = async function () {
  handleUpdateReadonlyJob()
  registerTimeout(() => setSnackbarData({ messageKey: 'locationsInventoryUpdated' }), 250)
}

const handleAddToBatchSuccess = async () => {
  refreshJobHistory()
  handleUpdateReadonlyJob()
}

const handleApprovalRejectedSuccess = async () => {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  setSnackbar({ type: SnackType.INFO, messageKey: 'rejected' })
}

const handleJobAddedFailure = async () => {
  handleUpdateReadonlyJob()
  registerTimeout(
    () =>
      setSnackbarData({
        type: SnackType.ERROR,
        messageKey: 'jobAddedToBatchFailure',
      }),
    250,
  )
}

/**
 * Goes up the parent component chain to tell `Ticket.vue` to update the schema ID.
 */
function updateSchemaButtonClicked() {
  useLatestCustomFieldsSchema()
}

const setEmailDialogVisibility = (isVisible: boolean) => {
  isEmailDialogVisible.value = isVisible
}

const handleNotificationSent = () => {
  setSnackbarData({ messageKey: 'notificationSent' })
}

const openJobDetailsEdit = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isEditingJobDetailsBottomSheetVisible: true,
  })
  resetCustomFieldsSchemaId()
}
const showFormSelection = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isSelectingFormBottomSheetVisible: true,
  })
}
const showFormSubmission = (formSubmissionUuid: string) => {
  selectedFormSubmissionId.value = formSubmissionUuid
}
const editJobFormSubmission = (formUuid: string, formSubmissionUuid: string) => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    formSubmissionBottomSheetState: {
      isVisible: true,
      isEditing: true,
      formUuid,
      formSubmissionUuid,
      stopId: null,
    },
  })
}
const fillJobFormSubmission = (formUuid: string) => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    formSubmissionBottomSheetState: {
      isVisible: true,
      isEditing: true,
      formUuid,
      formSubmissionUuid: null,
      stopId: null,
    },
  })
}
const closeFormSubmission = () => {
  selectedFormSubmissionId.value = null
}
const fillFormRequirement = (requirement: JobAssignmentFormRequirement) => {
  isFillingFormRequirementSubmission.value = true
  selectedFormRequirement.value = requirement
}
const viewFormRequirementSubmission = (requirement: JobAssignmentFormRequirement) => {
  selectedFormRequirement.value = requirement
}
const closeFillFormRequirement = () => {
  selectedFormRequirement.value = null
  isFillingFormRequirementSubmission.value = false
}
const closeFormRequirementSubmission = () => {
  selectedFormRequirement.value = null
}
const selectNewForm = ({ formId }) => {
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    formSubmissionBottomSheetState: {
      isVisible: true,
      isEditing: true,
      formUuid: formId,
      formSubmissionUuid: null,
      stopId: null,
    },
  })
}
const closeSchemaBasedForm = () => {
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
}
const processAttachFormSubmission = async (formSubmissionUuid: string) => {
  try {
    if (!jobUuid.value) {
      return
    }
    const stopId = bottomSheetStates.value.formSubmissionBottomSheetState.stopId
    if (stopId) {
      await attachFormSubmissionToStop(jobUuid.value, stopId, {
        form_id: bottomSheetStates.value.formSubmissionBottomSheetState.formUuid!,
        submission_id: formSubmissionUuid,
        created_at: moment().toISOString(),
      })
      await queryClient.invalidateQueries(['stops', 'byJobId', jobUuid.value])
    } else {
      await attachFormSubmissionId({
        jobId: jobUuid.value,
        formId: bottomSheetStates.value.formSubmissionBottomSheetState.formUuid,
        formSubmissionId: formSubmissionUuid,
      })
    }
    registerTimeout(() => setSnackbarData({ messageKey: 'attachedForm' }), 250)
  } catch (error) {
    registerTimeout(
      () =>
        setSnackbarData({
          type: SnackType.ERROR,
          messageKey: 'attachFormError',
        }),
      250,
    )
  } finally {
    jobBottomSheetStatesStore.clearJobBottomSheetStates()
  }
}

const attachFormRequirement = async (formSubmissionId: string) => {
  if (selectedFormRequirement.value === null) return

  try {
    await attachFormRequirementSubmission({
      jobId: jobUuid.value,
      formRequirementId: selectedFormRequirement.value.id,
      formSubmissionId,
    })
    registerTimeout(() => setSnackbarData({ messageKey: 'attachedFormRequirement' }), 250)
  } catch (error) {
    registerTimeout(
      () =>
        setSnackbarData({
          type: SnackType.ERROR,
          messageKey: 'attachFormRequirementError',
        }),
      250,
    )
  } finally {
    jobBottomSheetStatesStore.clearJobBottomSheetStates()
  }
}

const updatedAttachedFormSubmission = () => {
  closeFormSubmission()
  handleUpdateReadonlyJob()
  setSnackbarData({
    messageKey: 'updatedAttachedFormSubmission',
  })
}

const updatedFormRequirementSubmission = () => {
  closeFormRequirementSubmission()
  setSnackbarData({
    messageKey: 'updatedFormRequirementSubmission',
  })
}

const unsupportedFileSelected = (messageKey: string) => {
  setSnackbarData({ type: SnackType.ERROR, messageKey })
}

const errorUploadingAttachment = (messageKey: string) => {
  setSnackbarData({ type: SnackType.ERROR, messageKey })
}

const getAdhocFormSubmissionSecondaryText = (formSubmission) => {
  const userName = getUserBySid.value(formSubmission.submittedBy)?.name
  const uploadedTime = formatDateTime(formSubmission.submittedOn)

  return userName ? `${userName} on ${uploadedTime}` : `${uploadedTime}`
}
const ticketsError = computed(() => store.state.tickets.error)

const handleActionBarClick = async (action: ActionClickData) => {
  switch (action.type) {
    case ActionType.UNARCHIVE:
    case ActionType.ARCHIVE:
      await toggleArchived()
      break
    case ActionType.VIEW_PROJECT:
      viewProject()
      break
    case ActionType.REMOVE_FROM_PROJECT:
      await removeJobFromProject()
      break
    case ActionType.REMOVE_FROM_BATCH:
      await removeFromBatch()
      break
    case ActionType.SEND_NOTIFICATION:
      setEmailDialogVisibility(true)
      break
    case ActionType.STATUS_CHANGE:
      await updateStatus(action.value)
      break
    case ActionType.EDIT:
      emit('on-click-edit')
      break
    default:
      break
  }

  action.complete()
}

const { mutateAsync: bulkArchiveAsync } = useBulkArchiveMutation()
const { mutateAsync: unarchiveAsync } = useUnarchiveMutation()
const toggleArchived = async () => {
  if (!job.value) {
    return
  }

  const successKey = job.value.isArchived ? 'unarchived' : 'archived'
  const errorKey = job.value.isArchived ? 'unarchiveFailed' : 'archiveFailed'

  try {
    if (job.value.isArchived) {
      await unarchiveAsync({ jobId: job.value.uuid })
    } else {
      await bulkArchiveAsync({ jobIds: [job.value.uuid] })
    }

    await handleUpdateReadonlyJob()
    refreshJobHistory()

    setSnackbar({ type: SnackType.INFO, messageKey: successKey })
  } catch (error: any) {
    if (error.response?.data?.detail) {
      setSnackbar({
        customMessage: error.response.data.detail.toString(),
        type: SnackType.ERROR,
      })
    } else {
      setSnackbar({ messageKey: errorKey, type: SnackType.ERROR })
    }
  }
}

const pushToProject = (projectId: string) => {
  router.push({
    name: projectSideSheetRoute.value,
    params: { projectId },
  })
}
const addToProject = (projectId: string) => {
  pushToProject(projectId)
  handleUpdateReadonlyJob()
}

const viewProject = () => {
  const projectId = job.value?.projectId
  if (!projectId) return
  pushToProject(projectId)
}

const { mutateAsync: removeFromProject, error: removeFromProjectError } =
  removeJobFromProjectMutation()
const removeJobFromProject = async () => {
  const projectId = job.value?.projectId
  const jobId = job.value?.id

  if (!projectId || !jobId) return

  await removeFromProject({ projectId, jobNumber: jobId })
  if (removeFromProjectError.value) {
    setSnackbarData({
      type: SnackType.ERROR,
      messageKey: 'removeJobFromProjectError',
    })
  } else {
    await handleUpdateReadonlyJob()
  }
}

const { mutateAsync: removeJobsFromBatch } = useRemoveJobsFromInvoiceMutation()
const removeFromBatch = async () => {
  if (!job.value?.invoiceId) return
  try {
    await removeJobsFromBatch({
      id: job.value.invoiceId,
      jobIds: [job.value.uuid],
    })
    await handleUpdateReadonlyJob()
    refreshJobHistory()
    setSnackbar({
      type: SnackType.INFO,
      messageKey: 'removedJobFromBatch',
    })
  } catch (e) {
    setSnackbar({
      type: SnackType.ERROR,
      messageKey: 'removedJobFromBatchFailure',
    })
  }
}

const updateStatus = async (jobStatus: JobStatus) => {
  const jobId = job.value?.id
  if (!jobId) return
  if (jobStatus === COMPLETED && !formsWithSubmissionInfo.value.every((f) => f.isFilledOut)) {
    setSnackbarData({
      messageKey: 'ironSightFormsNotAllFilled',
      type: SnackType.ERROR,
    })
    return
  }

  await updateJobStatus({ id: jobId, jobStatus })

  if (!ticketsError.value) {
    setSnackbar({ messageKey: 'statusUpdated' })
    await handleUpdateReadonlyJob()
    refreshJobHistory()
  } else {
    const jobStatusText = {
      [JobStatus.ACTIVE]: 'activated',
      [JobStatus.COMPLETED]: 'completed',
      [JobStatus.CANCELLED]: 'cancelled',
      [JobStatus.UNCANCELLED]: 'uncancelled',
      [JobStatus.PAUSED]: 'paused',
    }
    setSnackbarData({
      type: SnackType.ERROR,
      messageKey: 'genericError',
      customMessage: jobStatusText[jobStatus] ? `Job cannot be ${jobStatusText[jobStatus]}` : null, // Default to generic error message
    })
  }
}

const handleUpdateReadonlyJob = async () => {
  if (!ticketsError.value) {
    await fetchJob(props.id)
    emit('job-saved', { jobNumber: props.id })
    invalidateJobsList()
  } else {
    setSnackbarData({
      type: SnackType.ERROR,
      messageKey: 'genericError',
    })
  }
}

const invalidateJobsList = () => {
  const queryKeySet = new Set([
    JOBS_FILTERED_QUERY_KEY,
    JOBS_FILTERED_COUNT_QUERY_KEY,
    BUCKET_COUNT_QUERY_KEY,
    'jobs',
    'projects',
  ])

  queryClient.invalidateQueries({
    predicate: (query) => queryKeySet.has(query.queryKey[0] as string),
  })
}

const submittedForApproval = () => {
  refreshJobHistory()
  handleUpdateReadonlyJob()
  registerTimeout(
    () =>
      setSnackbarData({
        type: SnackType.INFO,
        messageKey: 'submittedForApproval',
      }),
    250,
  )
}

function setSnackbar({
  type = SnackType.INFO,
  messageKey = null,
  customMessage = null, // customMessage will override messageKey unless customMessage = null
}: {
  type?: SnackType
  messageKey?: string | null
  customMessage?: string | null
}) {
  registerTimeout(() => setSnackbarData({ type, messageKey, customMessage }), 250)
}

const queryClient = useQueryClient()
const refreshJobHistory = () => {
  queryClient.invalidateQueries({
    queryKey: ['history', 'byJobNumber', job.value?.id],
  })
}

defineExpose({ rootSchema })
</script>

<style scoped>
.displayed-job-id {
  max-width: 250px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.add-text {
  font-family: Montserrat, sans-serif;
  font-weight: bold;
  color: var(--color-grey--light);
}
</style>
